import { Gem, RefreshCw, Save, Trophy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { LadderChallenge } from "./ui/LadderChallenge";
import type { APIClient } from "./api";
import { DetailWindow } from "./DetailWindow";
import type { GrowthProfile, LadderResult, LadderView } from "./types";

export function LadderPanel({
  api,
  growth,
  onChanged,
}: {
  api: APIClient;
  growth: GrowthProfile;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [view, setView] = useState<LadderView | null>(null),
    [result, setResult] = useState<LadderResult | null>(null);
  const banned = new Set([
    "mend",
    "greater_heal",
    "mass_mend",
    "drain",
    "life_drain",
    "wood_blessing",
  ]);
  const choices = growth.skills.filter(
    (s) => s.kind === "active" && s.unlocked && !banned.has(s.id),
  );
  const [skills, setSkills] = useState(() => growth.skillSlots.filter((id) => !banned.has(id)));
  const busyRef = useRef(false);
  useEffect(() => {
    if (open)
      void api
        .ladder()
        .then(setView)
        .catch((e) => setError(String(e)));
  }, [api, open]);
  const act = async (fn: () => Promise<LadderResult>, show = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const next = await fn();
      if (show) setResult(next);
      setView(await api.ladder());
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  return (
    <section className="ladder-entry">
      <button className="secondary-command" onClick={() => setOpen(true)}>
        <Trophy size={18} />
        天梯
      </button>
      {open && (
        <DetailWindow
          labelledBy="ladder-title"
          onClose={() => setOpen(false)}
          className="ladder-window"
          header={
            <h2 id="ladder-title">
              <Trophy size={20} />
              天梯
            </h2>
          }
        >
          <div className="ladder-summary">
            <strong>
              {view?.self ? `排名 ${view.self.rank} · ${view.self.rating} 分` : "未登记配置"}
            </strong>
            <span>
              剩余挑战 {Math.max(0, (view?.limit ?? 5) - (view?.used ?? 0))}/{view?.limit ?? 5}
            </span>
            <button
              className="icon-button"
              title="刷新天梯"
              aria-label="刷新天梯"
              disabled={busy}
              onClick={() =>
                void api
                  .ladder()
                  .then(setView)
                  .catch((e) => setError(String(e)))
              }
            >
              <RefreshCw size={16} />
            </button>
          </div>
          <LadderChallenge
            view={view}
            busy={busy}
            onChallenge={() => void act(() => api.challengeLadder(), true)}
          />
          <details className="ladder-build" open={!view?.self}>
            <summary>对战配置 · 禁止治疗法术</summary>
            <div className="ladder-skills">
              {choices.map((s) => (
                <label key={s.id}>
                  <input
                    type="checkbox"
                    checked={skills.includes(s.id)}
                    disabled={busy || (!skills.includes(s.id) && skills.length >= 3)}
                    onChange={() =>
                      setSkills((v) =>
                        v.includes(s.id) ? v.filter((id) => id !== s.id) : [...v, s.id],
                      )
                    }
                  />
                  {s.name}
                </label>
              ))}
            </div>
            <button
              className="primary-command compact"
              disabled={busy || skills.length === 0}
              onClick={() => void act(() => api.registerLadder(skills))}
            >
              <Save size={16} />
              保存当前配置
            </button>
          </details>
          <button
            className="secondary-command compact"
            disabled={busy || !view || view.purchased}
            onClick={() => {
              if (window.confirm(`消耗 ${view?.price} 钻石，增加今天 5 次挑战？`))
                void act(() => api.purchaseLadderAttempts());
            }}
          >
            <Gem size={16} />
            {view?.purchased ? "今日已扩至10次" : `${view?.price ?? 20} 钻石 · 增加5次`}
          </button>
          {error && <p role="alert">{error}</p>}
          {result && (
            <section className="ladder-result" role="status">
              <strong>
                {result.duel.winner === 0 ? "胜利" : result.duel.winner === 1 ? "战败" : "平局"}
              </strong>
              <span>
                {result.rating} 分 · +{result.crystal} 结晶
              </span>
              <details>
                <summary>战报</summary>
                {result.duel.frames
                  .filter((_, i) => i % 5 === 0 || i === result.duel.frames.length - 1)
                  .map((f, i) => (
                    <div key={i}>
                      {(f.at / 1000).toFixed(1)}s · {result.duel.names[0]} {f.hp[0]} HP /{" "}
                      {result.duel.names[1]} {f.hp[1]} HP
                    </div>
                  ))}
              </details>
            </section>
          )}
          <div className="ladder-opponents">
            {view?.entries.map((e) => (
              <div key={e.userId}>
                <b>{e.rank}</b>
                <span>
                  {e.name}
                  <small>{e.rating} 分</small>
                </span>
              </div>
            ))}
          </div>
          {view?.entries.length === 0 && <p>暂无登记玩家</p>}
        </DetailWindow>
      )}
    </section>
  );
}
